import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  getPublicPrograms,
  computeDistance,
  createReservation,
  getAvailableSlots,
  searchAres,
  getTravelPricing,
  PublicProgram,
  ReservationResult,
  AresItem,
  TravelPricing,
} from "../../lib/publicApi";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import StarField from "../../components/StarField";
import { useToast } from "../../components/Toast";
import {
  addMonths,
  czLongDate,
  czMonthYear,
  endOfMonth,
  monthGrid,
  startOfMonth,
  ymd,
} from "../../lib/dates";

// ─── Typy ────────────────────────────────────────────────

type ProgramSelection = { programId: string; childrenCount: number };

type Address = {
  display: string;
  street: string;
  city: string;
  zip: string;
  country: string;
  lat: number | null;
  lon: number | null;
};

type Billing = {
  enabled: boolean;
  name: string;
  ico: string;
  dic: string;
  street: string;
  city: string;
  zip: string;
  country: string;
};

const EMPTY_ADDRESS: Address = {
  display: "",
  street: "",
  city: "",
  zip: "",
  country: "CZ",
  lat: null,
  lon: null,
};

const EMPTY_BILLING: Billing = {
  enabled: false,
  name: "",
  ico: "",
  dic: "",
  street: "",
  city: "",
  zip: "",
  country: "CZ",
};

const STEPS = [
  { n: 1, title: "Programy", icon: "✨" },
  { n: 2, title: "Adresa", icon: "📍" },
  { n: 3, title: "Termín", icon: "🗓️" },
  { n: 4, title: "Kontakt", icon: "👤" },
  { n: 5, title: "Souhrn", icon: "🚀" },
];

export default function Reservation() {
  const toast = useToast();
  const [step, setStep] = useState(1);
  const [programs, setPrograms] = useState<PublicProgram[]>([]);

  // Stav kroků
  const [selections, setSelections] = useState<ProgramSelection[]>([]);
  const [kgName, setKgName] = useState("");
  const [addr, setAddr] = useState<Address>(EMPTY_ADDRESS);
  const [distance, setDistance] = useState<{
    oneWayKm: number;
    roundTripKm: number;
    min: number | null;
  } | null>(null);
  const [distanceLoading, setDistanceLoading] = useState(false);
  const [pricing, setPricing] = useState<TravelPricing>({ pricePerKmCzk: 8, maxDistanceKm: 100 });
  const [date, setDate] = useState<string>("");
  const [startTime, setStartTime] = useState<string>("");
  const [availableDays, setAvailableDays] = useState<Record<string, string[]>>({});
  const [calendarMonth, setCalendarMonth] = useState<Date>(startOfMonth(new Date()));
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<"BANK_TRANSFER" | "CASH">("BANK_TRANSFER");
  const [billing, setBilling] = useState<Billing>(EMPTY_BILLING);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ReservationResult | null>(null);

  // Načti programy + ceník
  useEffect(() => {
    getPublicPrograms()
      .then(setPrograms)
      .catch((e) => toast.show("error", e.message));
    getTravelPricing()
      .then(setPricing)
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Načti dostupné sloty pro aktuálně zobrazený měsíc (+-1 měsíc buffer, aby
  // při přechodu přes hranici měsíce nevznikala prázdná okna).
  useEffect(() => {
    if (selections.length === 0) return;
    const from = ymd(addMonths(calendarMonth, -1));
    const to = ymd(endOfMonth(addMonths(calendarMonth, 1)));
    setSlotsLoading(true);
    getAvailableSlots(from, to)
      .then((days) => {
        const map: Record<string, string[]> = {};
        for (const [d, arr] of Object.entries(days)) {
          map[d] = arr.map((s) => s.startTime).sort();
        }
        setAvailableDays(map);
      })
      .catch((e) => toast.show("error", e.message))
      .finally(() => setSlotsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selections.length, calendarMonth]);

  // Výpočet ceny + celkové doby
  const computed = useMemo(() => {
    let programsPrice = 0;
    let totalMin = 0;
    let setup = 0;
    let teardown = 0;
    for (const sel of selections) {
      const p = programs.find((x) => x.id === sel.programId);
      if (!p) continue;
      const blocks = Math.ceil(sel.childrenCount / p.maxChildrenPerSlot);
      const price = Math.max(sel.childrenCount * p.pricePerChildCzk, p.minPriceCzk);
      programsPrice += price;
      totalMin += blocks * p.durationMinutes;
      setup = Math.max(setup, p.setupMinutes);
      teardown = Math.max(teardown, p.teardownMinutes);
    }
    totalMin += setup + teardown;
    return { programsPrice, totalMin };
  }, [selections, programs]);

  const slotsNeeded = Math.ceil(computed.totalMin / 30);

  // Cena dopravy = round-trip km × Kč/km (souhlasí s backendem)
  const travelPrice = distance ? distance.roundTripKm * pricing.pricePerKmCzk : null;

  const viableStarts = useMemo(() => {
    if (!date || slotsNeeded === 0) return [];
    const starts = availableDays[date] ?? [];
    const toMin = (t: string) => {
      const [h, m] = t.split(":").map(Number);
      return h * 60 + m;
    };
    const result: string[] = [];
    for (let i = 0; i <= starts.length - slotsNeeded; i++) {
      let ok = true;
      for (let k = 0; k < slotsNeeded - 1; k++) {
        if (toMin(starts[i + k + 1]) - toMin(starts[i + k]) !== 30) {
          ok = false;
          break;
        }
      }
      if (ok) result.push(starts[i]);
    }
    return result;
  }, [date, availableDays, slotsNeeded]);

  // Set dnů, které mají alespoň 1 po sobě jdoucí AVAILABLE řadu délky `slotsNeeded`.
  const availableDateSet = useMemo(() => {
    const out = new Set<string>();
    const toMin = (t: string) => {
      const [h, m] = t.split(":").map(Number);
      return h * 60 + m;
    };
    for (const [d, starts] of Object.entries(availableDays)) {
      for (let i = 0; i <= starts.length - slotsNeeded; i++) {
        let ok = true;
        for (let k = 0; k < slotsNeeded - 1; k++) {
          if (toMin(starts[i + k + 1]) - toMin(starts[i + k]) !== 30) {
            ok = false;
            break;
          }
        }
        if (ok) {
          out.add(d);
          break;
        }
      }
    }
    return out;
  }, [availableDays, slotsNeeded]);

  async function fetchDistance(a: Address) {
    if (!a.city || !a.street) {
      setDistance(null);
      return;
    }
    setDistanceLoading(true);
    try {
      const res = await computeDistance({
        lat: a.lat ?? undefined,
        lon: a.lon ?? undefined,
        street: a.street,
        city: a.city,
        zip: a.zip,
        country: a.country,
      });
      setDistance({
        oneWayKm: res.oneWayKm,
        roundTripKm: res.roundTripKm,
        min: res.durationMin,
      });
    } catch (err: any) {
      setDistance(null);
      toast.show("error", err.message ?? "Nepodařilo se spočítat vzdálenost.");
    } finally {
      setDistanceLoading(false);
    }
  }

  function addProgram(id: string) {
    if (selections.some((s) => s.programId === id)) return;
    setSelections((xs) => [...xs, { programId: id, childrenCount: 15 }]);
  }
  function removeProgram(id: string) {
    setSelections((xs) => xs.filter((s) => s.programId !== id));
  }
  function setChildren(id: string, n: number) {
    setSelections((xs) => xs.map((s) => (s.programId === id ? { ...s, childrenCount: n } : s)));
  }

  async function submit() {
    if (!date || !startTime) {
      toast.show("error", "Vyber termín.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await createReservation({
        date,
        startTime,
        kindergarten: {
          name: kgName,
          street: addr.street,
          city: addr.city,
          zip: addr.zip || undefined,
          country: addr.country,
          lat: addr.lat ?? undefined,
          lon: addr.lon ?? undefined,
        },
        programs: selections,
        contact: {
          name: contactName,
          email: contactEmail,
          phone: contactPhone || undefined,
        },
        paymentMethod,
        billing: billing.enabled
          ? {
              name: billing.name || undefined,
              ico: billing.ico || undefined,
              dic: billing.dic || undefined,
              street: billing.street || undefined,
              city: billing.city || undefined,
              zip: billing.zip || undefined,
              country: billing.country || undefined,
            }
          : undefined,
        note: note || undefined,
      });
      setResult(r);
      setStep(6);
    } catch (err: any) {
      toast.show("error", err.message ?? "Odeslání selhalo.");
    } finally {
      setSubmitting(false);
    }
  }

  // Result stránka
  if (step === 6 && result) {
    return <SuccessPage result={result} />;
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-obloha-indigo-950 via-obloha-indigo-900 to-obloha-indigo-950 text-white">
      {/* Pozadí: hvězdy + orbs */}
      <StarField density={0.00008} />
      <div className="pointer-events-none fixed left-1/4 top-1/4 -z-0 h-96 w-96 rounded-full bg-purple-500/10 blur-3xl" />
      <div className="pointer-events-none fixed right-1/4 top-1/2 -z-0 h-96 w-96 rounded-full bg-obloha-gold-500/10 blur-3xl" />

      {/* Header */}
      <header className="relative z-10 border-b border-white/10 backdrop-blur-md">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2 text-lg font-semibold">
            <span className="text-2xl">🌌</span>
            Kouzelná Obloha
          </Link>
          <Link to="/" className="text-sm text-slate-300 hover:text-white">
            ← Zpět
          </Link>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-4xl px-6 py-8 md:py-12">
        <div className="mb-8 text-center">
          <div className="mb-3 inline-block rounded-full border border-obloha-gold-400/30 bg-obloha-gold-500/10 px-4 py-1 text-xs font-semibold uppercase tracking-wider text-obloha-gold-400">
            Rezervace termínu
          </div>
          <h1 className="bg-gradient-to-b from-white to-obloha-indigo-200 bg-clip-text text-4xl font-bold text-transparent md:text-5xl">
            Vytvořme společně dětem
            <br />
            <span className="bg-gradient-to-r from-obloha-gold-400 to-obloha-gold-300 bg-clip-text text-transparent">
              nezapomenutelný zážitek
            </span>
          </h1>
        </div>

        <StepsBar current={step} />

        {/* ═══ STEP 1: Programy ═══ */}
        {step === 1 && (
          <StepPanel>
            <StepHeader
              title="Jaký program zvolíte?"
              subtitle="Vyberte jeden nebo více programů a u každého zadejte počet dětí. Cena i čas se spočítá automaticky."
            />

            <div className="space-y-3">
              {programs.map((p) => {
                const sel = selections.find((s) => s.programId === p.id);
                const selected = !!sel;
                return (
                  <div
                    key={p.id}
                    className={`group relative overflow-hidden rounded-2xl border transition-all ${
                      selected
                        ? "border-obloha-gold-400/60 bg-gradient-to-r from-obloha-gold-500/10 to-transparent shadow-lg shadow-obloha-gold-500/10"
                        : "border-white/10 bg-white/5 hover:border-white/30 hover:bg-white/10"
                    }`}
                  >
                    <div className="flex items-start gap-4 p-5">
                      <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-obloha-indigo-800">
                        {p.coverImageUrl ? (
                          <img
                            src={p.coverImageUrl}
                            alt=""
                            className="h-full w-full object-cover transition-transform group-hover:scale-110"
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center text-3xl">✨</div>
                        )}
                      </div>

                      <div className="flex-1">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <h3 className="text-lg font-semibold">{p.name}</h3>
                          <div className="text-xs text-slate-400">
                            {p.durationMinutes} min · až {p.maxChildrenPerSlot} dětí · od{" "}
                            {p.minPriceCzk.toLocaleString("cs-CZ")} Kč
                          </div>
                        </div>
                        {p.description && (
                          <p className="mt-1 text-sm text-slate-300 line-clamp-2">{p.description}</p>
                        )}

                        {sel ? (
                          <div className="mt-3 flex flex-wrap items-center gap-3">
                            <label className="text-sm text-slate-300">Dětí:</label>
                            <div className="inline-flex overflow-hidden rounded-full border border-white/20">
                              <button
                                type="button"
                                onClick={() => setChildren(p.id, Math.max(1, sel.childrenCount - 1))}
                                className="h-9 w-9 text-white hover:bg-white/10"
                              >
                                –
                              </button>
                              <input
                                type="number"
                                min={1}
                                value={sel.childrenCount}
                                onChange={(e) =>
                                  setChildren(p.id, Math.max(1, Number(e.target.value) || 1))
                                }
                                className="w-16 bg-transparent text-center text-sm text-white focus:outline-none"
                              />
                              <button
                                type="button"
                                onClick={() => setChildren(p.id, sel.childrenCount + 1)}
                                className="h-9 w-9 text-white hover:bg-white/10"
                              >
                                +
                              </button>
                            </div>
                            {sel.childrenCount > p.maxChildrenPerSlot && (
                              <span className="rounded-full bg-amber-400/20 px-2 py-0.5 text-xs text-amber-300">
                                +{Math.ceil(sel.childrenCount / p.maxChildrenPerSlot) - 1}× opakování
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => removeProgram(p.id)}
                              className="ml-auto text-sm text-red-400 hover:text-red-300"
                            >
                              Odebrat
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => addProgram(p.id)}
                            className="mt-3 rounded-full border border-white/20 bg-white/5 px-4 py-1.5 text-sm font-medium text-white hover:bg-white/10"
                          >
                            + Přidat do rezervace
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {programs.length === 0 && (
                <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-slate-300">
                  Programy se právě připravují.
                </div>
              )}
            </div>

            <PriceBar
              programsPrice={computed.programsPrice}
              totalMin={computed.totalMin}
              oneWayKm={null}
              roundTripKm={null}
              travelPrice={null}
              show={selections.length > 0}
            />

            <NavBar
              backDisabled
              nextDisabled={selections.length === 0}
              onNext={() => setStep(2)}
            />
          </StepPanel>
        )}

        {/* ═══ STEP 2: Adresa ═══ */}
        {step === 2 && (
          <StepPanel>
            <StepHeader
              title="Kam vás poctíme návštěvou?"
              subtitle="Napište název školky a vyberte adresu z nabídky. Vzdálenost spočítáme automaticky."
            />

            <div className="space-y-5">
              <DarkField label="Název školky" required>
                <input
                  className="input-dark"
                  value={kgName}
                  onChange={(e) => setKgName(e.target.value)}
                  placeholder="MŠ Sluníčko"
                  required
                />
              </DarkField>

              <DarkField label="Adresa školky" required hint="Vyberte z nabídky — získáme GPS souřadnice pro přesný výpočet trasy.">
                <AddressAutocomplete
                  value={addr.display}
                  onChange={(v) => setAddr((a) => ({ ...a, display: v }))}
                  onSelect={(it) => {
                    const next: Address = {
                      display: it.label,
                      street: it.street,
                      city: it.city,
                      zip: it.zip,
                      country: it.country || "CZ",
                      lat: it.lat,
                      lon: it.lon,
                    };
                    setAddr(next);
                    fetchDistance(next);
                  }}
                  className="input-dark"
                />
              </DarkField>

              {(distanceLoading || distance) && (
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  {distanceLoading ? (
                    <div className="flex items-center gap-2 text-sm text-slate-300">
                      <Spinner /> Počítám trasu…
                    </div>
                  ) : (
                    distance && (
                      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                        <div className="flex items-center gap-3 text-slate-300">
                          <span className="text-xl">📍</span>
                          <div>
                            <div className="font-semibold text-white">
                              {distance.oneWayKm} km od našeho sídla
                            </div>
                            <div className="text-xs text-slate-400">
                              {distance.roundTripKm} km tam i zpět
                              {distance.min != null && ` · cesta ~${distance.min} min`}
                            </div>
                          </div>
                        </div>
                        {travelPrice != null && (
                          <div className="text-right">
                            <div className="text-xs uppercase text-slate-400">Doprava</div>
                            <div className="font-bold text-white">
                              {travelPrice.toLocaleString("cs-CZ")} Kč
                            </div>
                            <div className="text-[10px] text-slate-500">
                              {distance.roundTripKm} km × {pricing.pricePerKmCzk} Kč
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  )}
                </div>
              )}
            </div>

            <PriceBar
              programsPrice={computed.programsPrice}
              totalMin={computed.totalMin}
              oneWayKm={distance?.oneWayKm ?? null}
              roundTripKm={distance?.roundTripKm ?? null}
              travelPrice={travelPrice}
              show={true}
            />

            <NavBar
              onBack={() => setStep(1)}
              onNext={() => setStep(3)}
              nextDisabled={!kgName || !addr.street || !addr.city || !distance}
            />
          </StepPanel>
        )}

        {/* ═══ STEP 3: Termín ═══ */}
        {step === 3 && (
          <StepPanel>
            <StepHeader
              title="Kdy k vám zamíříme?"
              subtitle={`Potřebujeme ${formatMinutes(computed.totalMin)} volna pro vaši rezervaci. Zelené dny mají dostatek volných slotů.`}
            />

            <MonthCalendar
              month={calendarMonth}
              onChangeMonth={(m) => {
                setCalendarMonth(m);
                setDate("");
                setStartTime("");
              }}
              availableDateSet={availableDateSet}
              selectedDate={date}
              onSelectDate={(d) => {
                setDate(d);
                setStartTime("");
              }}
              loading={slotsLoading}
            />

            {date && (
              <div className="mt-2 rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm">
                    <span className="text-slate-400">Zvolený den: </span>
                    <strong className="text-white">
                      {czLongDate(new Date(date + "T00:00:00Z"))}
                    </strong>
                  </div>
                  {viableStarts.length > 0 && (
                    <span className="text-xs text-slate-400">
                      {viableStarts.length}{" "}
                      {viableStarts.length === 1 ? "možný čas" : viableStarts.length < 5 ? "možné časy" : "možných časů"}
                    </span>
                  )}
                </div>
                <div className="mt-4">
                  <div className="mb-2 text-xs uppercase text-slate-400">Čas začátku</div>
                  <div className="flex flex-wrap gap-2">
                    {viableStarts.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setStartTime(t)}
                        className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
                          startTime === t
                            ? "border-obloha-gold-400 bg-obloha-gold-500 text-obloha-indigo-950 shadow-lg shadow-obloha-gold-500/30"
                            : "border-white/10 bg-white/5 text-white hover:border-white/30 hover:bg-white/10"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <PriceBar
              programsPrice={computed.programsPrice}
              totalMin={computed.totalMin}
              oneWayKm={distance?.oneWayKm ?? null}
              roundTripKm={distance?.roundTripKm ?? null}
              travelPrice={travelPrice}
              show={true}
            />

            <NavBar
              onBack={() => setStep(2)}
              onNext={() => setStep(4)}
              nextDisabled={!date || !startTime}
            />
          </StepPanel>
        )}

        {/* ═══ STEP 4: Kontakt + platba + fakturace ═══ */}
        {step === 4 && (
          <StepPanel>
            <StepHeader
              title="Poslední maličkosti"
              subtitle="Jak se vám ozveme a jak to uhradíte."
            />

            <div className="space-y-5">
              <Section title="Kontaktní osoba">
                <div className="grid gap-4 md:grid-cols-2">
                  <DarkField label="Vaše jméno" required>
                    <input
                      className="input-dark"
                      value={contactName}
                      onChange={(e) => setContactName(e.target.value)}
                      required
                    />
                  </DarkField>
                  <DarkField label="E-mail" required hint="Potvrzení, faktura.">
                    <input
                      type="email"
                      className="input-dark"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      required
                    />
                  </DarkField>
                  <DarkField label="Telefon">
                    <input
                      type="tel"
                      className="input-dark"
                      value={contactPhone}
                      onChange={(e) => setContactPhone(e.target.value)}
                    />
                  </DarkField>
                </div>
                <div className="mt-4">
                  <DarkField label="Poznámka (volitelně)" hint="Přístup, rozměry sálu, cokoliv důležitého…">
                    <textarea
                      className="input-dark min-h-20"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                    />
                  </DarkField>
                </div>
              </Section>

              <Section title="Způsob platby">
                <div className="grid gap-3 md:grid-cols-2">
                  <DarkPaymentCard
                    selected={paymentMethod === "BANK_TRANSFER"}
                    onClick={() => setPaymentMethod("BANK_TRANSFER")}
                    icon="🧾"
                    title="Na fakturu"
                    subtitle="Po akci, splatnost 14 dnů, převodem"
                  />
                  <DarkPaymentCard
                    selected={paymentMethod === "CASH"}
                    onClick={() => setPaymentMethod("CASH")}
                    icon="💵"
                    title="Hotově v den akce"
                    subtitle="Částku předáte v hotovosti, dostanete pokladní doklad"
                  />
                </div>
              </Section>

              <BillingSection billing={billing} setBilling={setBilling} toast={toast} />
            </div>

            <NavBar
              onBack={() => setStep(3)}
              onNext={() => setStep(5)}
              nextDisabled={!contactName || !contactEmail}
            />
          </StepPanel>
        )}

        {/* ═══ STEP 5: Souhrn ═══ */}
        {step === 5 && (
          <StepPanel>
            <StepHeader title="Všechno v pořádku?" subtitle="Rychlá kontrola před odesláním." />

            <div className="space-y-4 rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur">
              <SummaryRow label="Termín">
                {czLongDate(new Date(date + "T00:00:00Z"))} · {startTime} ({formatMinutes(computed.totalMin)})
              </SummaryRow>
              <SummaryRow label="Školka">
                <div className="font-medium text-white">{kgName}</div>
                <div className="text-xs text-slate-400">{addr.display}</div>
                {distance && (
                  <div className="text-xs text-slate-400">
                    {distance.oneWayKm} km od nás ({distance.roundTripKm} km tam i zpět)
                  </div>
                )}
              </SummaryRow>
              <SummaryRow label="Programy">
                <ul className="space-y-1">
                  {selections.map((s) => {
                    const p = programs.find((x) => x.id === s.programId);
                    if (!p) return null;
                    const price = Math.max(s.childrenCount * p.pricePerChildCzk, p.minPriceCzk);
                    return (
                      <li key={s.programId} className="flex justify-between text-sm">
                        <span className="text-slate-300">
                          {p.name} — {s.childrenCount} dětí
                        </span>
                        <span className="tabular-nums font-medium">
                          {price.toLocaleString("cs-CZ")} Kč
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </SummaryRow>
              <SummaryRow label="Kontakt">
                {contactName} · {contactEmail}
                {contactPhone && ` · ${contactPhone}`}
              </SummaryRow>
              <SummaryRow label="Platba">
                {paymentMethod === "BANK_TRANSFER"
                  ? "🧾 Fakturou po akci, splatnost 14 dnů"
                  : "💵 Hotově v den akce"}
              </SummaryRow>
              {billing.enabled && (billing.name || billing.ico) && (
                <SummaryRow label="Fakturační údaje">
                  <div className="font-medium text-white">{billing.name}</div>
                  {billing.ico && <div className="text-xs text-slate-400">IČO: {billing.ico}</div>}
                  {billing.dic && <div className="text-xs text-slate-400">DIČ: {billing.dic}</div>}
                  {(billing.street || billing.city) && (
                    <div className="text-xs text-slate-400">
                      {billing.street}, {billing.zip} {billing.city}
                    </div>
                  )}
                </SummaryRow>
              )}
            </div>

            <PriceBar
              programsPrice={computed.programsPrice}
              totalMin={computed.totalMin}
              oneWayKm={distance?.oneWayKm ?? null}
              roundTripKm={distance?.roundTripKm ?? null}
              travelPrice={travelPrice}
              show={true}
              prominent
            />

            <NavBar
              onBack={() => setStep(4)}
              onNext={submit}
              nextLabel={submitting ? "Odesílám…" : "🚀 Odeslat rezervaci"}
              nextDisabled={submitting}
            />

            <p className="mt-4 text-center text-xs text-slate-400">
              Rezervace je nezávazná dokud ji nepotvrdíme e-mailem. Pokud by termín nevyhovoval,
              stačí odpovědět na potvrzovací e-mail.
            </p>
          </StepPanel>
        )}
      </main>
    </div>
  );
}

// ═══ Komponenty layoutu ═══════════════════════════════════

function StepsBar({ current }: { current: number }) {
  const progress = ((current - 1) / (STEPS.length - 1)) * 100;

  return (
    <div className="mb-8 rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
      {/* Progress line */}
      <div className="relative mb-4 h-1 rounded-full bg-white/10">
        <div
          className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-r from-obloha-gold-400 to-obloha-gold-500 transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Steps */}
      <div className="flex items-center justify-between text-xs">
        {STEPS.map((s) => {
          const active = current === s.n;
          const done = current > s.n;
          return (
            <div
              key={s.n}
              className={`flex flex-col items-center gap-1 ${
                active ? "text-white" : done ? "text-obloha-gold-400" : "text-slate-500"
              }`}
            >
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full border text-sm transition-all ${
                  active
                    ? "border-obloha-gold-400 bg-obloha-gold-500/30 shadow-lg shadow-obloha-gold-500/50"
                    : done
                    ? "border-obloha-gold-400 bg-obloha-gold-500 text-obloha-indigo-950"
                    : "border-white/20 bg-white/5"
                }`}
              >
                {done ? "✓" : s.icon}
              </div>
              <span className="hidden text-[10px] font-medium sm:block">{s.title}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StepPanel({ children }: { children: React.ReactNode }) {
  return (
    <div className="animate-in space-y-6 rounded-3xl border border-white/10 bg-white/5 p-6 shadow-2xl shadow-obloha-indigo-950/50 backdrop-blur-xl md:p-8">
      {children}
    </div>
  );
}

function StepHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div>
      <h2 className="text-2xl font-bold md:text-3xl">{title}</h2>
      {subtitle && <p className="mt-2 text-slate-300">{subtitle}</p>}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
      <h3 className="mb-4 text-base font-semibold">{title}</h3>
      {children}
    </div>
  );
}

function DarkField({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-slate-200">
        {label}
        {required && <span className="ml-1 text-obloha-gold-400">*</span>}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-slate-400">{hint}</span>}
    </label>
  );
}

function NavBar({
  onBack,
  onNext,
  nextLabel,
  nextDisabled,
  backDisabled,
}: {
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  backDisabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between pt-2">
      <button
        onClick={onBack}
        disabled={backDisabled}
        className="rounded-full border border-white/20 bg-white/5 px-6 py-2.5 text-sm font-medium text-white transition hover:bg-white/10 disabled:invisible"
      >
        ← Zpět
      </button>
      <button
        onClick={onNext}
        disabled={nextDisabled}
        className="group relative overflow-hidden rounded-full bg-gradient-to-r from-obloha-gold-500 to-obloha-gold-400 px-8 py-3 font-semibold text-obloha-indigo-950 shadow-lg shadow-obloha-gold-500/30 transition-all hover:scale-105 hover:shadow-obloha-gold-400/50 disabled:opacity-50 disabled:hover:scale-100"
      >
        <span className="relative z-10">{nextLabel ?? "Pokračovat →"}</span>
        <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent transition-transform duration-1000 group-hover:translate-x-full"></span>
      </button>
    </div>
  );
}

function PriceBar({
  programsPrice,
  totalMin,
  oneWayKm,
  roundTripKm,
  travelPrice,
  show,
  prominent,
}: {
  programsPrice: number;
  totalMin: number;
  oneWayKm: number | null;
  roundTripKm: number | null;
  travelPrice: number | null;
  show: boolean;
  prominent?: boolean;
}) {
  if (!show) return null;
  const total = programsPrice + (travelPrice ?? 0);
  return (
    <div
      className={`rounded-2xl border ${
        prominent
          ? "border-obloha-gold-400/50 bg-gradient-to-r from-obloha-gold-500/20 to-purple-500/10"
          : "border-white/10 bg-white/5"
      } p-4 backdrop-blur`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <div className="space-y-0.5 text-slate-300">
          <div>
            ⏱ Celkem <strong className="text-white">{formatMinutes(totalMin)}</strong>{" "}
            <span className="text-xs text-slate-400">(s montáží + demontáží)</span>
          </div>
          {oneWayKm != null && roundTripKm != null && (
            <div>
              📍 <strong className="text-white">{oneWayKm} km</strong>{" "}
              <span className="text-xs text-slate-400">
                · {roundTripKm} km tam i zpět
              </span>
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="space-y-0.5 text-xs text-slate-400">
            <div>
              Programy:{" "}
              <span className="tabular-nums text-slate-200">
                {programsPrice.toLocaleString("cs-CZ")} Kč
              </span>
            </div>
            {travelPrice != null && (
              <div>
                Doprava:{" "}
                <span className="tabular-nums text-slate-200">
                  {travelPrice.toLocaleString("cs-CZ")} Kč
                </span>
              </div>
            )}
          </div>
          <div
            className={`mt-1 font-bold tabular-nums ${
              prominent ? "text-3xl text-obloha-gold-300" : "text-xl text-white"
            }`}
          >
            {total.toLocaleString("cs-CZ")} Kč
          </div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            Celkem k úhradě
          </div>
        </div>
      </div>
    </div>
  );
}

function DarkPaymentCard({
  selected,
  onClick,
  icon,
  title,
  subtitle,
}: {
  selected: boolean;
  onClick: () => void;
  icon: string;
  title: string;
  subtitle: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative overflow-hidden rounded-xl border p-4 text-left transition ${
        selected
          ? "border-obloha-gold-400 bg-obloha-gold-500/10 shadow-lg shadow-obloha-gold-500/20"
          : "border-white/10 bg-white/5 hover:border-white/30 hover:bg-white/10"
      }`}
    >
      <div className="flex items-center gap-3">
        <div className="text-2xl">{icon}</div>
        <div className="flex-1">
          <div className="font-semibold text-white">{title}</div>
          <div className="text-xs text-slate-400">{subtitle}</div>
        </div>
        <div
          className={`h-5 w-5 shrink-0 rounded-full border-2 transition ${
            selected
              ? "border-obloha-gold-400 bg-obloha-gold-400"
              : "border-white/30 bg-transparent"
          }`}
        >
          {selected && (
            <div className="h-full w-full rounded-full border-2 border-obloha-indigo-950" />
          )}
        </div>
      </div>
    </button>
  );
}

function SummaryRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="border-b border-white/10 pb-3 last:border-0 last:pb-0">
      <div className="text-xs uppercase tracking-wider text-slate-400">{label}</div>
      <div className="mt-1 text-slate-200">{children}</div>
    </div>
  );
}

function Spinner() {
  return (
    <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
      <path
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

// ═══ Billing sekce (s ARES) ═══════════════════════════════

function BillingSection({
  billing,
  setBilling,
  toast,
}: {
  billing: Billing;
  setBilling: (b: Billing) => void;
  toast: ReturnType<typeof useToast>;
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<AresItem[]>([]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Click outside
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  async function doSearch(q: string) {
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const items = await searchAres(q);
      setResults(items);
    } catch (err: any) {
      toast.show("error", err.message ?? "ARES nedostupný.");
    } finally {
      setSearching(false);
    }
  }

  function fillFromAres(item: AresItem) {
    setBilling({
      ...billing,
      name: item.name,
      ico: item.ico,
      dic: item.dic,
      street: item.street,
      city: item.city,
      zip: item.zip,
      country: item.country || "CZ",
    });
    setSearchQuery(item.name);
    setOpen(false);
    toast.show("success", "Údaje doplněny z ARES.");
  }

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
      <label className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={billing.enabled}
          onChange={(e) => setBilling({ ...billing, enabled: e.target.checked })}
          className="mt-1 h-5 w-5 accent-obloha-gold-500"
        />
        <div>
          <div className="font-semibold">Fakturační údaje (volitelně)</div>
          <div className="text-xs text-slate-400">
            Pokud chcete fakturu vystavit na IČO, PO, zřizovatele apod. — doplňte údaje nebo
            použijte ARES.
          </div>
        </div>
      </label>

      {billing.enabled && (
        <div className="mt-5 space-y-4 border-t border-white/10 pt-5">
          {/* ARES vyhledávač */}
          <div ref={wrapRef} className="relative">
            <DarkField label="Rychlé vyplnění z ARES" hint="Zadejte IČO (8 číslic) nebo část názvu firmy/školky.">
              <div className="relative">
                <input
                  className="input-dark pr-10"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setOpen(true);
                    const t = setTimeout(() => doSearch(e.target.value), 350);
                    return () => clearTimeout(t);
                  }}
                  onFocus={() => setOpen(true)}
                  placeholder="12345678 nebo MŠ Sluníčko"
                />
                <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                  {searching ? <Spinner /> : <span className="text-slate-400">🔍</span>}
                </div>
              </div>
            </DarkField>

            {open && results.length > 0 && (
              <ul className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-xl border border-white/20 bg-obloha-indigo-900/95 shadow-xl backdrop-blur">
                {results.map((it, i) => (
                  <li key={i}>
                    <button
                      type="button"
                      onClick={() => fillFromAres(it)}
                      className="block w-full border-b border-white/5 px-4 py-3 text-left text-sm last:border-0 hover:bg-white/10"
                    >
                      <div className="font-medium text-white">{it.name}</div>
                      <div className="mt-0.5 text-xs text-slate-400">
                        IČO {it.ico}
                        {it.city && ` · ${it.city}`}
                        {it.pravniForma && ` · ${it.pravniForma}`}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {open && !searching && searchQuery.length >= 2 && results.length === 0 && (
              <div className="absolute z-20 mt-1 w-full rounded-xl border border-white/10 bg-obloha-indigo-900/95 p-4 text-sm text-slate-400 backdrop-blur">
                Nic nenalezeno. Vyplňte ručně níže.
              </div>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <DarkField label="Název / obchodní jméno">
              <input
                className="input-dark"
                value={billing.name}
                onChange={(e) => setBilling({ ...billing, name: e.target.value })}
              />
            </DarkField>
            <div className="grid grid-cols-2 gap-3">
              <DarkField label="IČO">
                <input
                  className="input-dark"
                  value={billing.ico}
                  onChange={(e) => setBilling({ ...billing, ico: e.target.value })}
                />
              </DarkField>
              <DarkField label="DIČ">
                <input
                  className="input-dark"
                  value={billing.dic}
                  onChange={(e) => setBilling({ ...billing, dic: e.target.value })}
                />
              </DarkField>
            </div>
            <DarkField label="Ulice a č.p.">
              <input
                className="input-dark"
                value={billing.street}
                onChange={(e) => setBilling({ ...billing, street: e.target.value })}
              />
            </DarkField>
            <div className="grid grid-cols-2 gap-3">
              <DarkField label="Město">
                <input
                  className="input-dark"
                  value={billing.city}
                  onChange={(e) => setBilling({ ...billing, city: e.target.value })}
                />
              </DarkField>
              <DarkField label="PSČ">
                <input
                  className="input-dark"
                  value={billing.zip}
                  onChange={(e) => setBilling({ ...billing, zip: e.target.value })}
                />
              </DarkField>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══ Success page ═════════════════════════════════════════

function SuccessPage({ result }: { result: ReservationResult }) {
  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-obloha-indigo-950 via-obloha-indigo-900 to-obloha-indigo-950 text-white">
      <StarField density={0.0001} />
      <div className="pointer-events-none fixed left-1/2 top-1/3 h-[500px] w-[500px] -translate-x-1/2 rounded-full bg-obloha-gold-500/20 blur-3xl" />

      <div className="relative mx-auto max-w-2xl px-6 py-20 text-center">
        <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-obloha-gold-500 to-obloha-gold-400 shadow-2xl shadow-obloha-gold-500/50">
          <span className="text-5xl">🌌</span>
        </div>

        <h1 className="mt-8 text-4xl font-bold md:text-5xl">
          <span className="bg-gradient-to-b from-white to-obloha-indigo-200 bg-clip-text text-transparent">
            Rezervace přijata!
          </span>
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-lg text-slate-200">
          Děkujeme. Ozveme se vám e-mailem s potvrzením, obvykle do 24 hodin.
        </p>

        <div className="mx-auto mt-10 max-w-md rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur">
          <div className="space-y-3 text-left text-sm">
            <SuccessRow label="Termín">
              {czLongDate(new Date(result.date))} · {result.startTime}–{result.endTime}
            </SuccessRow>
            <SuccessRow label="Vzdálenost">{result.distanceKm} km</SuccessRow>
            <SuccessRow label="Programy">
              {result.programsPriceCzk.toLocaleString("cs-CZ")} Kč
            </SuccessRow>
            <SuccessRow label="Doprava">
              {result.travelPriceCzk.toLocaleString("cs-CZ")} Kč
            </SuccessRow>
            <div className="flex items-center justify-between border-t border-white/20 pt-3 text-base font-bold">
              <span>Celkem</span>
              <span className="text-obloha-gold-300">
                {result.totalPriceCzk.toLocaleString("cs-CZ")} Kč
              </span>
            </div>
          </div>
        </div>

        <Link
          to="/"
          className="mt-10 inline-block rounded-full bg-gradient-to-r from-obloha-gold-500 to-obloha-gold-400 px-10 py-3 font-bold text-obloha-indigo-950 shadow-lg shadow-obloha-gold-500/30 transition-all hover:scale-105"
        >
          Zpět na úvod
        </Link>
      </div>
    </div>
  );
}

function SuccessRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-400">{label}</span>
      <span className="font-medium text-white">{children}</span>
    </div>
  );
}

// ═══ Kalendář (month view) ═══════════════════════════════

function MonthCalendar({
  month,
  onChangeMonth,
  availableDateSet,
  selectedDate,
  onSelectDate,
  loading,
}: {
  month: Date;
  onChangeMonth: (m: Date) => void;
  availableDateSet: Set<string>;
  selectedDate: string;
  onSelectDate: (ymdStr: string) => void;
  loading: boolean;
}) {
  const today = new Date();
  const todayKey = ymd(
    new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()))
  );

  // Neumožníme listovat do minulosti před aktuální měsíc
  const thisMonth = startOfMonth(new Date());
  const canGoPrev = month.getTime() > thisMonth.getTime();

  const cells = monthGrid(month);
  const currentMonthIdx = month.getUTCMonth();

  const weekdayLabels = ["Po", "Út", "St", "Čt", "Pá", "So", "Ne"];

  return (
    <div className="mx-auto w-full max-w-md rounded-3xl border border-white/10 bg-white/5 p-5 backdrop-blur sm:max-w-lg">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => onChangeMonth(addMonths(month, -1))}
          disabled={!canGoPrev}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white transition hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-white/5"
          aria-label="Předchozí měsíc"
        >
          ←
        </button>
        <div className="text-center">
          <div className="text-lg font-bold capitalize text-white">
            {czMonthYear(month)}
          </div>
          {loading && (
            <div className="text-[10px] uppercase tracking-wider text-slate-400">
              načítám sloty…
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => onChangeMonth(addMonths(month, 1))}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white transition hover:bg-white/10"
          aria-label="Další měsíc"
        >
          →
        </button>
      </div>

      {/* Weekday labels */}
      <div className="mt-4 grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400">
        {weekdayLabels.map((w) => (
          <div key={w} className="py-1">
            {w}
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className="mt-1 grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          const key = ymd(d);
          const inMonth = d.getUTCMonth() === currentMonthIdx;
          const isPast = key < todayKey;
          const isToday = key === todayKey;
          const available = availableDateSet.has(key);
          const selected = selectedDate === key;

          const disabled = !inMonth || isPast || !available;

          return (
            <button
              key={i}
              type="button"
              disabled={disabled}
              onClick={() => onSelectDate(key)}
              className={`aspect-square rounded-xl border text-sm font-medium transition ${
                selected
                  ? "border-obloha-gold-400 bg-gradient-to-br from-obloha-gold-500 to-obloha-gold-400 text-obloha-indigo-950 shadow-lg shadow-obloha-gold-500/30"
                  : available && inMonth && !isPast
                  ? "border-emerald-400/40 bg-emerald-500/10 text-white hover:border-emerald-400 hover:bg-emerald-500/20"
                  : !inMonth
                  ? "border-transparent text-slate-600"
                  : isPast
                  ? "border-white/5 text-slate-600"
                  : "border-white/5 text-slate-500"
              } ${isToday && !selected ? "ring-1 ring-obloha-gold-400/50" : ""}`}
            >
              <div className="flex h-full flex-col items-center justify-center gap-0.5">
                <span>{d.getUTCDate()}</span>
                {available && inMonth && !isPast && !selected && (
                  <span className="h-1 w-1 rounded-full bg-emerald-400" />
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-3 border-t border-white/10 pt-4 text-xs text-slate-400">
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded border border-emerald-400/40 bg-emerald-500/20" />{" "}
          volný den
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded border border-obloha-gold-400 bg-obloha-gold-500" />{" "}
          zvolený den
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded border border-white/5" /> obsazené / mimo rozsah
        </span>
      </div>
    </div>
  );
}

// ═══ Helpers ═════════════════════════════════════════════

function formatMinutes(n: number): string {
  const h = Math.floor(n / 60);
  const m = n % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
}
